import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { TrendUp, TrendDown, Warning, Bank, Hourglass, CheckCircle, XCircle, ClipboardText } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import api, { money, num } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { KpiCard, StatusChip, Money } from "../components/shared";
import { DashboardDrilldown } from "../components/DashboardDrilldown";
import { GettingStartedCard } from "../components/GettingStarted";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

const BAR_COLORS = ["#F5720D", "#F59034", "#FBB667", "#FCD4A6", "#FDE7CE"];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drill, setDrill] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });
  const { data: approvalStats } = useQuery({
    queryKey: ["approval-stats"],
    queryFn: async () => (await api.get("/approval-stats")).data,
  });
  const { data: alertsData } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
  });
  const { data: cash } = useQuery({
    queryKey: ["cash-flow"],
    queryFn: async () => (await api.get("/reports/cash-flow")).data,
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const k = data.kpis;
  const wi = data.weekly_insight;
  const fullName = user?.name || user?.email || "there";

  return (
    <div className="space-y-6 animate-fade-up">
      <GettingStartedCard />
      {/* Welcome headline */}
      <div className="border border-border bg-card p-5 sm:p-6" data-testid="welcome-headline">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight mt-1">
          Welcome back, <span data-testid="welcome-user-name">{fullName}</span>.
        </h2>
        {wi && (
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-3xl" data-testid="weekly-insight">
            In the past week your team booked <strong className="text-foreground tabular">{money(wi.sales_value)}</strong> across{" "}
            <strong className="text-foreground">{wi.doc_count}</strong> sales document{wi.doc_count === 1 ? "" : "s"} and collected{" "}
            <strong className="text-foreground tabular">{money(wi.collected)}</strong> in payments
            {wi.new_customers > 0 && <> · <strong className="text-foreground">{wi.new_customers}</strong> new customer{wi.new_customers === 1 ? "" : "s"}</>}
            {wi.change_pct != null && (
              <> · sales are{" "}
                <strong className={wi.change_pct >= 0 ? "text-emerald-600" : "text-red-600"}>
                  {wi.change_pct >= 0 ? "up" : "down"} {Math.abs(wi.change_pct)}%
                </strong> vs the prior week</>
            )}.
          </p>
        )}
      </div>

      <DashboardDrilldown metric={drill} onClose={() => setDrill(null)} />
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="kpi-revenue" onClick={() => setDrill("revenue")} label="Paid Revenue" value={money(k.total_revenue)} sub="Invoices settled" accent="text-primary" />
        <KpiCard testid="kpi-receivable" onClick={() => setDrill("receivables")} label="Receivables" value={money(k.outstanding_receivable)} sub="Owed by customers" />
        <KpiCard testid="kpi-payable" onClick={() => setDrill("payables")} label="Payables" value={money(k.outstanding_payable)} sub="Owed to vendors" />
        <KpiCard testid="kpi-inventory" onClick={() => setDrill("inventory")} label="Inventory Value" value={money(k.inventory_value)} sub={`${k.items} items`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="kpi-customers" onClick={() => setDrill("customers")} label="Customers" value={num(k.customers)} />
        <KpiCard testid="kpi-vendors" onClick={() => setDrill("vendors")} label="Vendors" value={num(k.vendors)} />
        <KpiCard testid="kpi-open-orders" onClick={() => setDrill("open_orders")} label="Open Orders" value={num(k.open_orders)} />
        <KpiCard testid="kpi-items" onClick={() => setDrill("items")} label="Stock Items" value={num(k.items)} />
      </div>

      {approvalStats && (
        <div className="border border-border bg-card" data-testid="approvals-tile">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <ClipboardText size={18} className="text-primary" weight="fill" />
            <h3 className="text-sm font-bold uppercase tracking-wide">Purchase Approvals</h3>
            <span className="text-xs text-muted-foreground">this month</span>
            <button onClick={() => navigate("/my-approvals")} className="ml-auto text-xs font-semibold text-primary hover:underline" data-testid="approvals-tile-inbox">My Approvals →</button>
          </div>
          <div className="grid grid-cols-3 divide-x divide-border">
            <button onClick={() => navigate("/my-approvals")} className="p-4 text-left hover:bg-secondary/40 transition-colors" data-testid="approvals-pending">
              <div className="flex items-center gap-1.5 text-amber-600"><Hourglass size={15} weight="bold" /><span className="overline-label">Pending</span></div>
              <p className="text-2xl font-black tabular mt-1">{num(approvalStats.pending)}</p>
            </button>
            <button onClick={() => setDrill("approved")} className="p-4 text-left hover:bg-secondary/40 transition-colors" data-testid="approvals-approved">
              <div className="flex items-center gap-1.5 text-emerald-600"><CheckCircle size={15} weight="bold" /><span className="overline-label">Approved</span></div>
              <p className="text-2xl font-black tabular mt-1">{num(approvalStats.approved)}</p>
            </button>
            <button onClick={() => setDrill("declined")} className="p-4 text-left hover:bg-secondary/40 transition-colors" data-testid="approvals-declined">
              <div className="flex items-center gap-1.5 text-rose-600"><XCircle size={15} weight="bold" /><span className="overline-label">Declined</span></div>
              <p className="text-2xl font-black tabular mt-1">{num(approvalStats.declined)}</p>
            </button>
          </div>
        </div>
      )}

      {alertsData?.total > 0 && (
        <div className="border border-border bg-card" data-testid="dashboard-alerts">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Warning size={18} className="text-red-500" weight="fill" />
            <h3 className="text-sm font-bold uppercase tracking-wide">Attention Needed</h3>
            <span className="text-xs text-muted-foreground">{alertsData.counts.high} high · {alertsData.counts.medium} medium</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {alertsData.alerts.slice(0, 6).map((a, i) => (
              <div key={i} className="bg-card px-4 py-3 flex items-start gap-2.5" data-testid="dashboard-alert">
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${a.severity === "high" ? "bg-red-500" : a.severity === "medium" ? "bg-amber-500" : "bg-blue-500"}`} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{a.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cash flow */}
      {cash && (
        <div className="border border-border bg-card" data-testid="cash-flow-panel">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Bank size={18} className="text-primary" weight="fill" />
            <h3 className="text-sm font-bold uppercase tracking-wide">Cash Flow Position</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
            <button onClick={() => setDrill("cash")} className="p-4 text-left hover:bg-secondary/40 transition-colors" data-testid="cf-cash">
              <p className="overline-label">Cash on Hand</p>
              <p className="text-xl font-black tabular mt-1">{money(cash.cash_on_hand)}</p>
            </button>
            <button onClick={() => setDrill("receivables")} className="p-4 text-left hover:bg-secondary/40 transition-colors" data-testid="cf-receivables">
              <p className="overline-label">+ Receivables</p>
              <p className="text-xl font-black tabular mt-1 text-emerald-600">{money(cash.receivables)}</p>
            </button>
            <button onClick={() => setDrill("payables")} className="p-4 text-left hover:bg-secondary/40 transition-colors" data-testid="cf-payables">
              <p className="overline-label">− Payables</p>
              <p className="text-xl font-black tabular mt-1 text-red-600">{money(cash.payables)}</p>
            </button>
            <button onClick={() => setDrill("projected")} className="p-4 text-left bg-primary/5 hover:bg-primary/10 transition-colors" data-testid="cf-projected">
              <p className="overline-label">Projected Position</p>
              <p className="text-xl font-black tabular mt-1 text-primary">{money(cash.projected_position)}</p>
            </button>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="overline-label">Last 6 months</p>
              <h3 className="text-lg font-bold tracking-tight">Revenue Trend</h3>
            </div>
            <TrendUp size={20} className="text-emerald-600" weight="bold" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_trend} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F5720D" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#F5720D" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
              <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="#F5720D" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-5">
          <p className="overline-label">By sales value</p>
          <h3 className="text-lg font-bold tracking-tight mb-4">Top Items</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.top_items} layout="vertical" margin={{ left: 0, right: 10 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Bar dataKey="amount" radius={[0, 2, 2, 0]}>
                {data.top_items.map((_, i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent + Low stock */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border bg-card">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-lg font-bold tracking-tight">Recent Sales Documents</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Doc No.</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recent_orders.map((o) => (
                <TableRow key={o.id} data-testid="recent-order-row">
                  <TableCell className="font-mono text-xs font-medium">{o.doc_no}</TableCell>
                  <TableCell className="text-sm">{o.customer_name}</TableCell>
                  <TableCell><StatusChip status={o.status} /></TableCell>
                  <TableCell className="text-right"><Money value={o.total} className="text-sm font-medium" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="border border-border bg-card">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Warning size={18} className="text-amber-500" weight="fill" />
            <h3 className="text-lg font-bold tracking-tight">Low Stock</h3>
          </div>
          <div className="divide-y divide-border">
            {data.low_stock.length === 0 && (
              <p className="text-sm text-muted-foreground p-5">All items above reorder point.</p>
            )}
            {data.low_stock.map((i) => (
              <div key={i.sku} className="flex items-center justify-between px-5 py-3" data-testid="low-stock-row">
                <div>
                  <p className="text-sm font-medium">{i.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{i.sku}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-red-600 tabular">{num(i.quantity)}</p>
                  <p className="text-xs text-muted-foreground">min {num(i.reorder_point)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
